/**
 * Two doors, deliberately different (`core-api` §4).
 *
 * A parent proves an address and a password. A child proves a PIN and nothing
 * else — the trusted device already says who they are, so the child screen has
 * no field for a name, an e-mail or an identifier. There is nowhere for a child
 * to type who they are, which is the partition made visible.
 */

import { useEffect, useState } from "react";

import { useSession } from "@/app/session";
import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button, Card, ErrorNote, Screen } from "@/ui/primitives";

export function ParentSignIn() {
  const { signInParent } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInParent(email, password);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "Connexion impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen title="Espace parents">
      <Card>
        <form onSubmit={submit}>
          <label className="field">
            <span className="field__label">Adresse e-mail</span>
            <input
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Mot de passe</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          <Button type="submit" disabled={busy}>
            {busy ? "Un instant…" : "Se connecter"}
          </Button>
        </form>
      </Card>
    </Screen>
  );
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "←"];

export function ChildSignIn() {
  const { signInChild } = useSession();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState<string | null>(null);

  // Whose tablet is this? The device already answers that — the whole PIN flow
  // rests on it (`core-api` §4.2) — so the screen says it instead of greeting a
  // stranger. On an unknown device the call refuses, and we simply stay silent:
  // it is not the child's job to be told the tablet was never enrolled.
  useEffect(() => {
    let cancelled = false;
    void call("deviceOwner")
      .then((device) => {
        if (!cancelled) setOwner(device.display_name);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const press = async (key: string) => {
    if (busy) return;
    if (key === "←") {
      setPin((value) => value.slice(0, -1));
      return;
    }
    if (!key) return;
    const next = `${pin}${key}`.slice(0, 6);
    setPin(next);
    if (next.length < 6) return;

    setBusy(true);
    setError(null);
    try {
      await signInChild(next);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "Ce code ne marche pas.");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen title={owner ? `Bonjour ${owner}` : "Ton code"}>
      <Card>
        {owner ? <p className="muted">Ton code</p> : null}
        <p className="pin" aria-label={`${pin.length} chiffres sur 6`}>
          {Array.from({ length: 6 }, (_, index) => (
            <span key={index} className="pin__dot" data-filled={index < pin.length ? "true" : "false"} />
          ))}
        </p>
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        <div className="keypad" role="group" aria-label="Chiffres">
          {KEYS.map((key, index) => (
            <button
              key={index}
              type="button"
              className="keypad__key"
              disabled={!key || busy}
              onClick={() => void press(key)}
            >
              {key}
            </button>
          ))}
        </div>
      </Card>
      <p className="muted small">
        <a href="/connexion">Espace parents</a>
      </p>
    </Screen>
  );
}
